import { useEffect, useState } from 'react';
import { pbLog } from '../utils/log';
import { settings } from 'settings';
import Fetch from 'utils/fetch';

import { flatten } from 'utils/array';
import { getParcelSortInHub } from 'fetch/registerParcelInHub';
import { fetchOrders } from '../fetch/orderFetch';
import {
  BundleJson,
  BundlesPageJson,
  BundleStats,
  BundleStatus,
  InstructionType,
} from 'types/bundleTypes';
import { Order, Region } from '../types/orderTypes';
import { ReqEnum } from '../const/requestStatus';
import dayjs from 'dayjs';

const log = pbLog('containers:BundlesFetch');

interface OrderBundleIds {
  orderId: string;
  bundleId: string;
}

interface SortListItem {
  orderId: string;
  bundleId: string;
  shelfId: string;
  order: Order;
  courierName: string;
}

export interface ScanningItem extends SortListItem {
  hubSorted: boolean;
  parcelCounted: number;
  partnerScanCount: number;
  scannedParcelIds: string[];
  theMostPreciseCountOfKolli: number;
  partnerScanConfirmedAt: string;
}

export function useFetchScanningData(
  statusList = [
    BundleStatus.Published,
    BundleStatus.Proposed,
    BundleStatus.Accepted,
    BundleStatus.Completed,
  ].join(','),
  region?: Region,
  fetchTime?: dayjs.Dayjs
): [ScanningItem[], ReqEnum] {
  const [fetchStatus, setFetchStatus] = useState<ReqEnum>(ReqEnum.INIT);
  const [bundles, setBundles] = useState<BundleJson[]>([]);
  const [scanningData, setScanningData] = useState<ScanningItem[]>([]);

  useEffect(() => {
    log('Reset state');
    setFetchStatus(ReqEnum.PENDING);
    log('Fetching bundles');
    const fetchBundlesByStatus = async statusList => {
      const [bundles, scanningData, status] = await fetchAllBundles(
        statusList,
        region
      );
      log('Fetched %d bundles', bundles.length);
      setBundles(bundles);
      setScanningData(scanningData);
      setFetchStatus(status);
    };

    fetchBundlesByStatus(statusList);
  }, [bundles.length, statusList, region, fetchTime]);

  return [scanningData, fetchStatus];
}

export function useFetchBundles(
  statusList = [
    BundleStatus.Published,
    BundleStatus.Proposed,
    BundleStatus.Accepted,
    BundleStatus.Completed,
  ].join(','),
  region?: Region,
  fetchTime?: dayjs.Dayjs
): [BundleJson[], ReqEnum] {
  const [fetchStatus, setFetchStatus] = useState<ReqEnum>(ReqEnum.INIT);
  const [bundles, setBundles] = useState<BundleJson[]>([]);

  useEffect(() => {
    log('Reset state');
    setFetchStatus(ReqEnum.PENDING);
    log('Fetching bundles');
    const fetchBundlesByStatus = async statusList => {
      const [bundles, , status] = await fetchAllBundles(statusList, region);
      log('Fetched %d bundles', bundles.length);
      setBundles(bundles);
      setFetchStatus(status);
    };

    fetchBundlesByStatus(statusList);
  }, [bundles.length, statusList, region, fetchTime]);

  return [bundles, fetchStatus];
}

export function useFetchBundleStats(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
): [BundleStats[], ReqEnum] {
  const [fetchStatus, setFetchStatus] = useState<ReqEnum>(ReqEnum.INIT);
  const [bundleStats, setBundleStats] = useState<BundleStats[]>([]);

  const startDate = start.format('YYYY-MM-DD');
  const endDate = end.format('YYYY-MM-DD');

  useEffect(() => {
    setFetchStatus(ReqEnum.PENDING);

    const fetchBundlesStats = async (startDate, endDate) => {
      const [bundleStatsList, status] = await fetchBundleStats(
        startDate,
        endDate
      );

      setBundleStats(bundleStatsList);
      setFetchStatus(status);
    };

    fetchBundlesStats(startDate, endDate);
  }, [startDate, endDate]);

  return [bundleStats, fetchStatus];
}

async function fetchBundleStats(
  start: string,
  end: string
): Promise<[BundleStats[], ReqEnum]> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle/report?start=${start}&end=${end}`,
  });

  if (response.ok) {
    const bundleStats = (await response.json()) as BundleStats[];
    return [bundleStats, ReqEnum.SUCCESS];
  } else {
    return [[], ReqEnum.ERROR];
  }
}

async function fetchAllBundles(
  statusList: string,
  region?: Region
): Promise<[BundleJson[], ScanningItem[], ReqEnum]> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle?status=${statusList}${
      region ? `&region=${region}` : ''
    }`,
  });

  if (response.ok) {
    const bundleResponse = (await response.json()) as BundlesPageJson;

    const bundles = bundleResponse.items;

    const scanningData = await fetchTheHubSortData(bundles);
    return [bundles, scanningData, ReqEnum.SUCCESS];
  } else {
    return [[], [], ReqEnum.ERROR];
  }
}

async function fetchActiveBundleOrders(
  instructions: OrderBundleIds[]
): Promise<Order[]> {
  try {
    return await fetchOrders(
      instructions.map(instruction => instruction.orderId)
    );
  } catch (e) {
    return [];
  }
}

async function makeListWithHubSortData(
  sortList: SortListItem[]
): Promise<ScanningItem[]> {
  const orderIds = sortList.map(item => item.orderId);
  const sortHubList = await getParcelSortInHub(orderIds);

  const mergedOrders = sortList.map(orderObj => {
    const hubSortObj = sortHubList.find(
      hubSort => Number(hubSort.orderId) === Number(orderObj.orderId)
    );

    return {
      ...orderObj,

      hubSorted: hubSortObj && hubSortObj.shelfId === orderObj.shelfId,
      parcelCounted: hubSortObj && hubSortObj.parcelCount,
      partnerScanCount: hubSortObj && hubSortObj.partnerScanCount,
      scannedParcelIds: hubSortObj && hubSortObj.scannedParcelIds,
      theMostPreciseCountOfKolli:
        hubSortObj &&
        (hubSortObj.partnerScanCount > 0
          ? hubSortObj.partnerScanCount
          : orderObj.order.packagesNumber),
      partnerScanConfirmedAt: hubSortObj && hubSortObj.partnerScanConfirmedAt,
    };
  });

  return mergedOrders;
}

async function fetchTheHubSortData(bundles: BundleJson[]) {
  const flattenedAndOnlyPickups: OrderBundleIds[] = flatten(
    bundles.map(bundle =>
      bundle.instructions
        .filter(instruction => instruction.type === InstructionType.Pickup)
        .map(instr => ({
          bundleId: bundle.id,
          orderId: instr.orderId,
        }))
    )
  );

  const orders = await fetchActiveBundleOrders(flattenedAndOnlyPickups);

  const sortList: SortListItem[] = flattenedAndOnlyPickups.map(instruction => {
    const bundle = bundles.find(item => item.id === instruction.bundleId);
    return {
      orderId: instruction.orderId,
      bundleId: instruction.bundleId,
      shelfId: bundle.shelfId,
      order: orders.find(order => order.id === instruction.orderId),
      courierName: bundle.assignedCourierName,
    };
  });

  const sortListWithHubSort = await makeListWithHubSortData(sortList);

  return sortListWithHubSort;
}
